import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchCryptoPrices } from "features/market/api/CryptoPrices";
import {
    fetchLatestRates,
    fetchHistoricalRates,
    type DollarQuote,
    type DollarType,
} from "features/market/api/DollarRates";

export interface DateRange {
    from: Date;
    to: Date;
}

export interface BitcoinPrice {
    usd?: number;
    usd_24h_change?: number;
}

interface UseDollarRatesReturn {
    rates: Record<DollarType, DollarQuote> | null;
    bitcoinPrice: BitcoinPrice | null;
    bitcoinLoading: boolean;
    historicalData: DollarQuote[];
    filteredData: DollarQuote[];
    loading: boolean;
    error: string | null;
    selectedType: DollarType;
    setSelectedType: (type: DollarType) => void;
    dateRange: DateRange;
    setDateRange: (range: DateRange) => void;
}

function defaultDateRange(): DateRange {
    const to = new Date();
    const from = new Date(to.getFullYear(), to.getMonth() - 3, 1);
    return { from, to };
}

export function useDollarRates(): UseDollarRatesReturn {
    const [rates, setRates] = useState<Record<DollarType, DollarQuote> | null>(null);
    const [bitcoinPrice, setBitcoinPrice] = useState<BitcoinPrice | null>(null);
    const [bitcoinLoading, setBitcoinLoading] = useState(true);
    const [historicalData, setHistoricalData] = useState<DollarQuote[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedType, setSelectedType] = useState<DollarType>("blue");
    const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchLatestRates()
            .then((data) => { if (!cancelled) setRates(data); })
            .catch((err) => { if (!cancelled) setError(String(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setBitcoinLoading(true);
        fetchCryptoPrices(["bitcoin"])
            .then((data) => {
                if (cancelled) return;
                const btc = data.bitcoin;
                if (btc?.usd != null || btc?.usd_24h_change != null) {
                    setBitcoinPrice({ usd: btc.usd, usd_24h_change: btc.usd_24h_change });
                } else {
                    setBitcoinPrice(null);
                }
            })
            .catch(() => {
                if (!cancelled) setBitcoinPrice(null);
            })
            .finally(() => {
                if (!cancelled) setBitcoinLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    const loadHistory = useCallback((type: DollarType) => {
        fetchHistoricalRates(type)
            .then(setHistoricalData)
            .catch(() => setHistoricalData([]));
    }, []);

    useEffect(() => {
        loadHistory(selectedType);
    }, [selectedType, loadHistory]);

    const filteredData = useMemo(() => {
        const fromStr = dateRange.from.toISOString().slice(0, 10);
        const toStr = dateRange.to.toISOString().slice(0, 10);
        return historicalData.filter((d) => d.fecha >= fromStr && d.fecha <= toStr);
    }, [historicalData, dateRange]);

    return {
        rates,
        bitcoinPrice,
        bitcoinLoading,
        historicalData,
        filteredData,
        loading,
        error,
        selectedType,
        setSelectedType,
        dateRange,
        setDateRange,
    };
}
